// M11-B2 (plan 325, task 1460): copy for the unpublish consequence dialog
// (M6-C consent-sheet idiom). The COUNTS come from the backend preflight
// (GET /api/profile/unpublish/preview — the same lookups the exit-market
// routine runs, no writes); this module only phrases them. It never derives
// a count and never fabricates a zero: while the preflight is loading or
// after it fails, the dialog states the consequences generically.

export interface UnpublishPreview {
  pendingRequests: number;
  openOffers: number;
  openWindows: number;
}

export type UnpublishPreviewStatus = "loading" | "loaded" | "error";

export interface UnpublishConsequenceInput {
  preview: UnpublishPreview | null;
  status: UnpublishPreviewStatus;
  /** True when the buyer has an active marketplace connection (four-state
   * publish block state 4). Unpublishing never ends it; disconnect is a
   * separate, deliberate action. */
  connected: boolean;
}

export const UNPUBLISH_GENERIC_CONSEQUENCE =
  "Any pending requests to agents will be withdrawn and any open offers will close.";

function plural(n: number, singular: string, pluralForm: string): string {
  return n === 1 ? singular : pluralForm;
}

export function buildUnpublishConsequences({
  preview,
  status,
  connected,
}: UnpublishConsequenceInput): string[] {
  const lines = ["Your passport will be hidden from agents."];

  if (status !== "loaded" || preview === null) {
    lines.push(UNPUBLISH_GENERIC_CONSEQUENCE);
  } else {
    const { pendingRequests, openOffers, openWindows } = preview;
    if (pendingRequests > 0) {
      lines.push(
        `${pendingRequests} pending ${plural(pendingRequests, "request", "requests")} to agents will be withdrawn.`,
      );
    }
    if (openOffers > 0) {
      lines.push(
        `${openOffers} open ${plural(openOffers, "offer", "offers")} will close.`,
      );
    }
    if (openWindows === 1) {
      lines.push("Your open agent-choice window will close.");
    } else if (openWindows > 1) {
      lines.push(`${openWindows} open agent-choice windows will close.`);
    }
    if (pendingRequests === 0 && openOffers === 0 && openWindows === 0) {
      lines.push("No pending requests or open offers to close.");
    }
  }

  lines.push(
    connected
      ? "Your connection with your agent stays active — to end it, use Disconnect."
      : "You can go live again anytime.",
  );
  return lines;
}
